import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Validators } from '@angular/forms';
import { noWhitespace } from '../../../../shared/validators/no-whitespace.validator';
import { SnackbarService } from '../../../../shared/services/snackbar/snackbar.service';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { MaintenanceInsuranceProvidersService } from './maintenance-insurance-providers-service/maintenance-insurance-providers.service';
import { MaintenanceInsuranceProvidersModel } from './maintenance-insurance-providers-models/maintenance-insurance-providers.models';
import { HttpStatusCode } from 'axios';
import { ConfirmPopupComponent } from '../../../agent-onboarding/new-rep-application/confirm-popup/confirm-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';


/** An insurance provider row — name + firm code are reference; user code is editable. */
export interface InsuranceProvider {
  savedRecordId?: number;
  id: any;
  name: string;
  firmCode: string;
  /** Optional qualifier shown beside the code (e.g. "Master Code", "No Firm Code"). */
  note?: string;
}

/** Reference list of providers, index-aligned to the section's form array. */
export const INSURANCE_PROVIDERS: InsuranceProvider[] = [
  { id: 1, name: 'SINGLIFE AVIVA Life', firmCode: '' },
  { id: 2, name: 'AVIVA Global', firmCode: '' },
  { id: 3, name: 'AIA', firmCode: '' },
  { id: 4, name: 'AXA Life', firmCode: '' },
  { id: 5, name: 'China Life', firmCode: '' },
  { id: 6, name: 'China Taiping', firmCode: '31500000' },
  { id: 7, name: 'Etiqa', firmCode: '20000' },
  { id: 8, name: 'FPI', firmCode: '' },
  { id: 9, name: 'Manulife', firmCode: 'B5800' },
  { id: 10, name: 'NTUC Income', firmCode: '583100' },
  { id: 11, name: 'RHI-BUPA', firmCode: '' },
  { id: 12, name: 'RHI', firmCode: '' },
  { id: 13, name: 'SingLife', firmCode: '' },
  { id: 14, name: 'Tokio Marine LIFE', firmCode: '00000838' },
  { id: 15, name: 'Zurich Life', firmCode: '' },
  { id: 16, name: 'HSBC [GI]', firmCode: '', note: 'WEF before Aug 2025' },
  { id: 17, name: 'AVIVA Navigator', firmCode: '86000004' },
  { id: 18, name: 'IFAST', firmCode: 'PIA00183', note: 'PIAS AMU Administrator Code' },
  { id: 19, name: 'IGP', firmCode: 'PIAFA00096', note: 'PIAS AMU Administrator Code' },
  { id: 20, name: 'AIG', firmCode: '' },
  { id: 21, name: 'CHUBB / ACE', firmCode: '' },
  { id: 22, name: 'AXA GI', firmCode: '' },
  { id: 23, name: 'EQ', firmCode: '' },
  { id: 24, name: 'LIBERTY', firmCode: '' },
  { id: 25, name: 'MSIG (G)', firmCode: '' },
  { id: 26, name: 'MSIG (Health)', firmCode: '' },
  { id: 27, name: 'MSIG (Dual)', firmCode: '' },
  { id: 28, name: 'MSIG (Dual) 16%', firmCode: '' },
  { id: 29, name: 'SOMPO', firmCode: '' },
  { id: 30, name: 'ALLIANZ', firmCode: 'BF000004', note: 'Master Code' },
  { id: 31, name: 'CHINA TAIPING', firmCode: 'BR0095A', note: 'Master Code' },
  { id: 32, name: 'QBE', firmCode: '04L1865', note: 'Master Code' },
  { id: 33, name: 'TMIS', firmCode: '2296DDA', note: 'Master Code' },
  { id: 34, name: 'DirectAsia', firmCode: '' },
  { id: 35, name: 'Allied World (New)', firmCode: '', note: 'Merimen' },
  { id: 36, name: 'Transamerica Life (Bermuda) Ltd', firmCode: '' },
  { id: 37, name: 'FWD (Life/GI)', firmCode: '', note: 'No Firm Code' }
];

/**
 * Insurance Providers maintenance section.
 *
 * Only providers that carry a user code are listed. Others are added on demand
 * from the dropdown at the top, which offers exactly the providers not already
 * listed — the two sets are always complements.
 */
@Component({
  selector: 'app-maintenance-insurance-providers',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatInputModule,
  ],
  templateUrl: './maintenance-insurance-providers.component.html',
  styleUrl: './maintenance-insurance-providers.component.scss'
})
export class MaintenanceInsuranceProvidersComponent implements OnInit {

  /** One `{ userCode }` group per provider, aligned by index to `providers`. */
  @Input() formArray!: FormArray;
  @Input() readonly = signal(false);
  @Output() save = new EventEmitter<string>();

  providers: InsuranceProvider[] = [];
  data = history.state;

  /** Provider indices shown in the list, in the order they were added. */
  listedIndices: number[] = [];

  /** Provider indices offered by the dropdown — everything not listed. */
  availableIndices: number[] = [];

  /** Bound to the dropdown; reset to null after each add so it reads as a picker. */
  selectedProviderIndex: number | null = null;

  private fb = inject(FormBuilder);
  private snackBarService = inject(SnackbarService);
  private maintenanceInsuranceProvidersService = inject(MaintenanceInsuranceProvidersService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private readonly matDialog = inject(MatDialog);
  setSprinner = signal(false);
  sessionStorageService: any;
  role: string | undefined;

  isVisible = signal(false);

  async saveSection(): Promise<void> {

    if (this.isVisible()) {
      return;
    }
    this.isVisible.set(true);

    if (this.listedIndices.length === 0) {
      this.snackBarService.openWarningSnackBar(
        'Please select at least one Insurance Provider.'
      );
      this.isVisible.set(false);
      return;
    }

    this.formArray.markAllAsTouched();

    if (this.formArray.invalid) {
      this.snackBarService.openWarningSnackBar(
        'Fill the required field.'
      );
      this.isVisible.set(false);
      return;
    }

    // skip API call if nothing changed
    if (!this.formArray.dirty) {
      this.snackBarService.openWarningSnackBar(
        'No changes to save.'
      );
      this.isVisible.set(false);
      return;
    }

    this.setSprinner.set(true);

    const insuranceProvidersModel = new MaintenanceInsuranceProvidersModel();

    const insuranceProvider = this.listedIndices.map((index) => ({
      id: this.providers[index].savedRecordId ?? 0,
      firm_id: this.providers[index].id,
      assigned_agent_code: this.userCodeControl(index).value
    }));

    insuranceProvidersModel.setPiasRepCode(this.data.applicant.repcode);
    insuranceProvidersModel.setInsuranceProvider(insuranceProvider);

    const responseData = await this.maintenanceInsuranceProvidersService
      .saveInsuranceProvidersData(insuranceProvidersModel)
      .then((response: string) => JSON.parse(response))
      .catch((error: any) => {
        console.error('Save API Error:', error);
        return error;
      });

    this.setSprinner.set(false);

    if (responseData.status === HttpStatusCode.Ok) {
      this.snackBarService.openSuccessSnackBar(
        'Insurance Provider saved successfully.'
      );

      // Refresh savedRecordId mapping right after save,
      // so remove works correctly without needing to leave & re-enter the section.
      await this.loadInsuranceProviders();

      this.save.emit('saved');

      this.applyReadonlyState();
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        this.snackBarService.openFailureSnackBar(
          "We couldn't process your request. Please try again"
        );
      }

      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar(
          "Session timed out. Try logging in again!"
        );
        this.router.navigate(['/login']);
      }
    }

    this.isVisible.set(false);
  }

  cancelSection(): void {

    if (this.isVisible()) {
      return;
    }

    this.isVisible.set(true);

    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Are you sure you want to cancel?',
        hidecancel: false,
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {

      this.isVisible.set(false);

      if (result?.data === true) {
        // Roll back anything added/edited locally that was never actually
        // persisted, so it doesn't reappear when the section is reopened.
        this.revertUnsavedChanges();
        this.save.emit('cancelled');
      }
    });

  }

  /** Clears local-only additions/edits (no savedRecordId) so cancel truly discards them. */
  private revertUnsavedChanges(): void {

    this.listedIndices.forEach((index) => {
      if (!this.providers[index].savedRecordId) {
        const control = this.userCodeControl(index);
        control.setValue('');
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });

    this.listedIndices = this.listedIndices.filter(
      (index) => !!this.providers[index].savedRecordId
    );

    this.syncAvailable();
    this.formArray.markAsPristine();
  }

  checkLength($event: KeyboardEvent): void {
    if ($event.code === 'Space' || $event.key === ' ') {
      $event.preventDefault();
      $event.stopPropagation();
    }
  }

  async ngOnInit(): Promise<void> {

    //  Master list fetch  providers array  gets build into dynamic
    await this.loadProvidersMasterList();

    // The parent form outlives this component — the editor is destroyed when the
    // section closes — so only add the rows that are missing and keep entered codes.
    while (this.formArray.length < this.providers.length) {
      this.formArray.push(this.fb.group({
        userCode: ['']
      }));
    }


    //  Existing saved data fetch and gets patch
    await this.loadInsuranceProviders();

    const localOnlyIndices = this.providers
      .map((_, index) => index)
      .filter((index) => !this.listedIndices.includes(index) && !!this.userCode(index).trim());

    this.listedIndices = [...this.listedIndices, ...localOnlyIndices];
    this.syncAvailable();


    this.applyReadonlyState();


  }

  //get master list dropdown
  async loadProvidersMasterList(): Promise<void> {
    const responseData = await this.maintenanceInsuranceProvidersService
      .getInsuranceProvidersList()
      .then((response: string) => JSON.parse(response))
      .catch((error: any) => {
        console.error('Master List API Error:', error);
        return null;
      });

    if (responseData?.data?.responsedata?.length) {
      this.providers = responseData.data.responsedata.map((item: any) => ({
        id: item.id,
        name: item.firmName,
        firmCode: item.masterCode
      }));
    } else {
      this.providers = INSURANCE_PROVIDERS;
    }
  }

  //get
  async loadInsuranceProviders(): Promise<void> {
    const insuranceProvidersModel = new MaintenanceInsuranceProvidersModel();
    insuranceProvidersModel.setPiasRepCode(this.data.applicant.repcode);

    const request = JSON.parse(insuranceProvidersModel.toString());

    const responseData = await this.maintenanceInsuranceProvidersService
      .getInsuranceProviders(request)
      .then((response: string) => JSON.parse(response))
      .catch((error: any) => {
        console.error('API Error:', error);
        return null;
      });

    if (responseData?.data?.responsedata) {
      const dedupedMap = new Map<number, any>();
      responseData.data.responsedata.forEach((item: any) => {
        if (!item.repcode) return;
        dedupedMap.set(item.firmId, item);
      });

      // Sort by saved record id (insertion order) so newly added providers
      // land last, regardless of the order the backend returned them in.
      const sortedItems = Array.from(dedupedMap.values())
        .sort((a, b) => a.id - b.id);

      sortedItems.forEach((item) => {
        const providerIndex = this.providers.findIndex(p => p.id === item.firmId);

        if (providerIndex === -1) return;

        // Always keep savedRecordId in sync with the backend, whether the
        // provider was already listed locally or not.
        this.providers[providerIndex].savedRecordId = item.id;

        // Only add to the visible list (and patch the user code) if it isn't
        // already there — avoids clobbering an in-progress edit.
        // if (!this.listedIndices.includes(providerIndex)) {
        //   this.listedIndices = [...this.listedIndices, providerIndex];
        //   this.userCodeControl(providerIndex).setValue(item.repcode);
        // }
        if (!this.listedIndices.includes(providerIndex)) {
          this.listedIndices = [...this.listedIndices, providerIndex];

          const control = this.userCodeControl(providerIndex);

          control.setValue(item.repcode);

          control.setValidators([
            Validators.required,
            noWhitespace
          ]);

          control.updateValueAndValidity();
        }
      });

      this.syncAvailable();
      this.cdr.detectChanges();

      // Re-apply readonly state after patching values in — patching can
      // otherwise leave freshly-added controls enabled.
      this.applyReadonlyState();


    }
  }

  /** Adds the picked provider to the list and takes it out of the dropdown. */
  addProvider(index: number | null): void {

    // Guarded here rather than via [disabled] so the picker doesn't flash
    // its disabled style while a save/remove is in flight.
    if (this.isVisible()) {
      this.selectedProviderIndex = null;
      return;
    }

    if (this.readonly() === true) {
      return;
    }

    if (index === null || index === undefined || this.listedIndices.includes(index)) {
      return;
    }
    this.listedIndices = [...this.listedIndices, index];

    this.userCodeControl(index).setValidators([
      Validators.required, noWhitespace]);

    this.userCodeControl(index).updateValueAndValidity();

    this.formArray.markAsDirty();

    this.syncAvailable();
    // Clear the picker so it shows its placeholder again, ready for the next add.
    this.selectedProviderIndex = null;

  }

  /** Removes a provider from the list and returns it to the dropdown. */
  removeProvider(index: number): void {

    if (this.isVisible()) {
      return;
    }

    if (this.readonly() === true) {
      return;
    }

    this.isVisible.set(true);

    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Are you sure you want to remove this Insurance Provider?',
        hidecancel: false,
      }
    });

    dialogRef.afterClosed().subscribe(async (result: any) => {
      if (result?.data !== true) {
        this.isVisible.set(false);
        return;
      }

      const savedRecordId = this.providers[index].savedRecordId;

      if (!savedRecordId) {
        this.listedIndices = this.listedIndices.filter((listed) => listed !== index);

        const control = this.userCodeControl(index);
        control.setValue('');
        control.clearValidators();
        control.updateValueAndValidity();

        this.formArray.markAsDirty();

        this.syncAvailable();
        this.snackBarService.openSuccessSnackBar('Provider removed successfully.');
        this.isVisible.set(false);
        return;
      }

      this.setSprinner.set(true);

      const deleteModel = new MaintenanceInsuranceProvidersModel();
      deleteModel.setId(savedRecordId);

      const responseData = await this.maintenanceInsuranceProvidersService
        .deleteInsuranceProvider(deleteModel)
        .then((response: string) => JSON.parse(response))
        .catch((error: any) => {
          console.error('Delete API Error:', error);
          return null;
        });

      this.setSprinner.set(false);

      if (responseData) {
        this.listedIndices = this.listedIndices.filter((listed) => listed !== index);

        const control = this.userCodeControl(index);
        control.setValue('');
        control.clearValidators();
        control.updateValueAndValidity();


        this.providers[index].savedRecordId = undefined;
        this.formArray.markAsDirty();

        this.syncAvailable();
        this.snackBarService.openSuccessSnackBar('Provider removed successfully.');
      } else {
        this.snackBarService.openFailureSnackBar("We couldn't process your request. Please try again");
      }
      this.isVisible.set(false);
    });

  }

  /** Bound per row with `[formControl]`, so no wrapper form group is needed here. */
  userCodeControl(index: number): FormControl {
    return this.formArray.at(index).get('userCode') as FormControl;
  }

  private userCode(index: number): string {
    return (this.userCodeControl(index).value as string | null) ?? '';
  }

  private syncAvailable(): void {
    this.availableIndices = this.providers
      .map((_, index) => index)
      .filter((index) => !this.listedIndices.includes(index));
  }

  private applyReadonlyState(): void {
    if (this.readonly() === true) {
      this.formArray?.disable();
    } else {
      this.formArray?.enable();
    }
  }
}